export const GAME = {
  width: 800,
  height: 600,
  backgroundColor: '#000000',
} as const;

export const PLAYER = {
  hp: 100,
  damagePerHit: 10,
  invulnMs: 500,
  speedPxPerSec: 300,
  yPercent: 0.85,
  size: 40,
  autoFireIntervalMs: 200,
  color: 0x4caf50,
} as const;

export const ENEMY = {
  hp: 30,
  speedPxPerSec: 120,
  size: 36,
  color: 0xef5350,
  spawnIntervalMs: 1500,
} as const;

export const BULLET = {
  speedPxPerSec: 600,
  damage: 10,
  size: 6,
  color: 0xffeb3b,
} as const;

export const SKILLS = {
  spreadShot: {
    cooldownMs: 5000,
    bulletCount: 3,
    fanDegrees: 15,
  },
} as const;

export const SCENE = {
  deathRestartDelayMs: 800,
} as const;

export const BATTLE = {
  waveSizes: [5, 7, 10] as const,
  spawnIntervalMs: 800,
  gapBetweenWavesMs: 2000,
} as const;

export const REWARD = {
  winFreeXP: 40,
  bossDefeatedFreeXP: 100,
} as const;

export const BOSS_BASE = {
  bulletSpeed: 350,
  bulletDamage: 15,
  bulletSize: 12,
} as const;

export const ASSETS = {
  // Stage backgrounds (top-down)
  stageBgSpace: 'stage-bg-space',
  // Horizontal stage backgrounds (1792x1024, faction-mapped)
  hStageCelestial: 'h-stage-celestial',
  hStageRunic: 'h-stage-runic',
  hStageTechno: 'h-stage-techno',
  hStageElemental: 'h-stage-elemental',
  hStageDominion: 'h-stage-dominion',
  // SVG fallback keys (always loaded; used when PNG assets are absent)
  playerTank: 'player-tank',
  enemyTank: 'enemy-tank',
  enemyFast: 'enemy-fast',
  enemyTankHeavy: 'enemy-tank-heavy',
  bulletNormal: 'bullet-normal',
  bulletMeteor: 'bullet-meteor',
  bossShuMo: 'boss-shu-mo',
  bossFuHun: 'boss-fu-hun',
  bossYuanSu: 'boss-yuan-su',
  // Tier-2 cross-faction bosses
  bossShuFu: 'boss-shu-fu',
  bossJiYuan: 'boss-ji-yuan',
  bossXingYu: 'boss-xing-yu',
  bossZiFu: 'boss-zi-fu',
  // Tier-3 semester bosses
  bossShiZhong: 'boss-shi-zhong',
  bossTianMing: 'boss-tian-ming',
  // Kenney Particle Pack — explosion atlas keys
  explosionSmoke: 'smoke_01',
  explosionFire: 'fire_01',
  explosionSpark: 'spark_06',
  explosionStar: 'star_05',
  // Kenney Top-Down Tanks Redux PNG keys (populated by scripts/fetch-assets.sh)
  // Falls back to SVG keys above if PNG load fails (run fetch script first).
  playerTankPng: 'player-tank-png',
  enemyNormalPng: 'enemy-normal-png',
  enemyFastPng: 'enemy-fast-png',
  enemyHeavyPng: 'enemy-heavy-png',
  // FEEL-B: 4-frame player tank track animation (Pollinations Flux, gitignored binary)
  playerTrack1: 'player-track-1',
  playerTrack2: 'player-track-2',
  playerTrack3: 'player-track-3',
  playerTrack4: 'player-track-4',
  // art-gen-1: Pollinations Flux per-faction player tank sprites
  // (256×256 PNG, Phaser scales down). Loaded by HorizontalBattleScene.preload();
  // _drawPlayer falls back to Graphics shapes when the texture is absent.
  tankPlayerCelestial: 'tank-player-celestial',
  tankPlayerRunic:     'tank-player-runic',
  tankPlayerTechno:    'tank-player-techno',
  tankPlayerElemental: 'tank-player-elemental',
  tankPlayerDominion:  'tank-player-dominion',
  // art-gen-1: Pollinations Flux enemy tank variants (basic / fast / heavy).
  tankEnemyBasic: 'tank-enemy-basic',
  tankEnemyFast:  'tank-enemy-fast',
  tankEnemyTank:  'tank-enemy-tank',
  // P01: packed atlas containing the 8 tank sprites above. Frame names match
  // the individual sprite asset keys (e.g. 'tank-player-celestial') so callers
  // can use either `add.image(x, y, 'tanks-atlas', 'tank-player-celestial')`
  // or — when the atlas isn't loaded (test harness / stripped build) — fall
  // back to `add.image(x, y, 'tank-player-celestial')` against the legacy
  // per-file textures still preloaded as backup.
  tanksAtlas: 'tanks-atlas',
} as const;

/** Per-variant stat multipliers applied on top of ENEMY base values. */
export const ENEMY_VARIANTS = {
  normal: { hpMult: 1, speedMult: 1.0, sizeMult: 1.0, assetKey: 'enemy-tank'      as const },
  fast:   { hpMult: 1, speedMult: 1.7, sizeMult: 0.8, assetKey: 'enemy-fast'       as const },
  heavy:  { hpMult: 3, speedMult: 0.5, sizeMult: 1.3, assetKey: 'enemy-tank-heavy' as const },
} as const;

export type EnemyVariant = keyof typeof ENEMY_VARIANTS;
